import React, { useEffect, useRef, useState } from "react";
import { useFillHeight } from "../lib/useFillHeight";
import CollapseBar, { BarTitle } from "../components/CollapseBar";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { Plus, Trash2, Pencil, Factory, ChevronDown, ChevronRight, MapPin } from "lucide-react";
import { trUpper } from "../lib/mw";
import { toast } from "sonner";

const BASE_FIELDS = [
  ["firma", "FİRMA ADI"], ["yetkili", "YETKİLİ"], ["tel", "TELEFON"], ["email", "EMAIL"],
  ["il", "İL"], ["ilce", "İLÇE"], ["adres", "ADRES"], ["vergi_dairesi", "VERGİ DAİRESİ"], ["vergi_no", "VERGİ NO"],
];
const PRICE_FIELDS = [["koli_fiyat", "KOLİ ₺"], ["palet_fiyat", "PALET ₺"], ["desi_fiyat", "DESİ ₺"], ["vade", "VADE (GÜN)"]];

export default function Suppliers() {
  const tableBoxRef = useRef(null);
  useFillHeight(tableBoxRef);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bolgeler: [] });
  const [confirm, setConfirm] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [cargos, setCargos] = useState([]);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/geo/provinces", { silent: true }).then((r) => setProvinces(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/cargo", { silent: true }).then((r) => setCargos(r.data.items || r.data || [])).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm({ bolgeler: [] }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r.data, bolgeler: r.data.bolgeler || [] }); setOpen(true); };
  const toggleBolge = (p) => setForm((f) => ({ ...f, bolgeler: (f.bolgeler || []).includes(p) ? f.bolgeler.filter((x) => x !== p) : [...(f.bolgeler || []), p] }));

  const save = async () => {
    if (!form.firma) { toast.error("FİRMA ADI GEREKLİ"); return; }
    if (editing) await api.put(`/suppliers/${editing.id}`, { data: form });
    else await api.post("/suppliers", { data: form });
    toast.success("TEDARİKÇİ KAYDEDİLDİ");
    setOpen(false); load();
  };
  const del = async () => { await api.delete(`/suppliers/${confirm}`); toast.success("SİLİNDİ"); setConfirm(null); load(); };

  const worksFor = (firma) => {
    const f = (firma || "").toLocaleUpperCase("tr-TR");
    return cargos.filter((c) => {
      const d = c.data || {};
      return [d.gonderici, d.alici, d.firma, d.musteri].some((x) => (x || "").toLocaleUpperCase("tr-TR") === f);
    });
  };

  return (
    <div className="mw-fade-up space-y-4">
      <BarTitle><div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-white">TEDARİKÇİ</h1>
          <p className="text-sm text-slate-400">{rows.length} TEDARİKÇİ · BÖLGE · KOLİ/PALET/DESİ FİYAT · VADE · TAŞINAN İŞLER</p>
        </div>
        <CollapseBar id="suppliers-actions" label="İŞLEMLER" className="flex-1 justify-end">
        <Button data-testid="add-supplier-button" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700">
          <Plus className="mr-1 h-4 w-4" /> YENİ TEDARİKÇİ
        </Button>
        </CollapseBar>
      </div></BarTitle>

      <div ref={tableBoxRef} className="overflow-auto rounded-xl border border-slate-800 bg-slate-900">
        <table className="mw-table w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-900">
            <tr className="border-b border-slate-800 bg-slate-800/50 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-3 py-3"></th><th className="px-3 py-3">FİRMA</th><th className="px-3 py-3">YETKİLİ</th><th className="px-3 py-3">TELEFON</th>
              <th className="px-3 py-3">BÖLGELER</th><th className="px-3 py-3 text-right">KOLİ ₺</th><th className="px-3 py-3 text-right">PALET ₺</th><th className="px-3 py-3 text-right">DESİ ₺</th>
              <th className="px-3 py-3">VADE</th><th className="px-3 py-3 text-right">İŞLEM</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500">TEDARİKÇİ YOK</td></tr>}
            {rows.map((r) => {
              const works = worksFor(r.data.firma);
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr data-testid={`supplier-row-${r.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="px-3 py-2.5">
                      <button data-testid={`supplier-expand-${r.id}`} onClick={() => setExpanded(isOpen ? null : r.id)} className="rounded p-1 text-slate-400 hover:bg-slate-700">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-slate-200"><span className="inline-flex items-center gap-1"><Factory className="h-3.5 w-3.5 text-indigo-300" />{r.data.firma}</span></td>
                    <td className="px-3 py-2.5 text-slate-300">{r.data.yetkili || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-300">{r.data.tel || "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex max-w-[260px] flex-wrap gap-1">
                        {(r.data.bolgeler || []).slice(0, 4).map((b) => <span key={b} className="rounded-full bg-indigo-500/20 px-2 py-0.5 text-[10px] font-semibold text-indigo-200">{b}</span>)}
                        {(r.data.bolgeler || []).length > 4 && <span className="text-[10px] text-slate-500">+{r.data.bolgeler.length - 4}</span>}
                        {(!r.data.bolgeler || r.data.bolgeler.length === 0) && <span className="text-slate-600">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-200">{r.data.koli_fiyat || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-200">{r.data.palet_fiyat || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-200">{r.data.desi_fiyat || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-300">{r.data.vade ? `${r.data.vade} GÜN` : "—"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`supplier-edit-${r.id}`} onClick={() => openEdit(r)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-blue-400"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setConfirm(r.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-950/40"><td colSpan={10} className="px-6 py-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-indigo-300"><MapPin className="h-3.5 w-3.5" /> ÇALIŞTIĞI BÖLGELER</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(r.data.bolgeler || []).length === 0 && <span className="text-xs text-slate-500">BÖLGE GİRİLMEMİŞ</span>}
                            {(r.data.bolgeler || []).map((b) => <span key={b} className="rounded-full bg-indigo-500/20 px-2.5 py-1 text-xs font-semibold text-indigo-200">{b}</span>)}
                          </div>
                          <div className="mt-3 text-xs text-slate-400">E-POSTA: {r.data.email || "—"} · V.D: {r.data.vergi_dairesi || "—"} · VNO: {r.data.vergi_no || "—"}</div>
                          <div className="text-xs text-slate-500">{r.data.adres || ""}</div>
                        </div>
                        <div>
                          <div className="mb-2 text-xs font-semibold uppercase text-emerald-300">TAŞINAN İŞLER ({works.length})</div>
                          <div className="max-h-40 overflow-auto rounded-lg border border-slate-800">
                            {works.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">BU TEDARİKÇİYLE EŞLEŞEN İŞ YOK</div>}
                            {works.map((w) => (
                              <div key={w.id} className="flex items-center justify-between border-b border-slate-800/60 px-3 py-1.5 text-xs text-slate-300">
                                <span>#{w.id} {w.data?.gonderici || w.data?.firma || ""} → {w.data?.alici || w.data?.il || ""}</span>
                                <span className="font-mono text-slate-400">{w.data?.tutar || w.data?.desi || ""}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">{editing ? "TEDARİKÇİ DÜZENLE" : "YENİ TEDARİKÇİ"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {BASE_FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs text-slate-400">{l}</Label>
                <Input data-testid={`supplier-${k}`} className="h-10 border-slate-700 bg-slate-800 text-slate-100" value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: k === "email" ? e.target.value : trUpper(e.target.value) }))} />
              </div>
            ))}
            {PRICE_FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs text-slate-400">{l}</Label>
                <Input data-testid={`supplier-${k}`} className="h-10 border-slate-700 bg-slate-800 text-slate-100" value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">ÇALIŞTIĞI BÖLGELER (İLLER) — RESİMLİ HARİTADA İŞARETLENİR</Label>
            <div className="max-h-44 overflow-auto rounded-lg border border-slate-700 bg-slate-800/40 p-2">
              <div className="flex flex-wrap gap-1.5">
                {provinces.map((p) => {
                  const on = (form.bolgeler || []).includes(p);
                  return <button key={p} type="button" data-testid={`supplier-bolge-${p}`} onClick={() => toggleBolge(p)} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${on ? "bg-indigo-500/30 text-indigo-200 ring-1 ring-indigo-400/50" : "bg-slate-800 text-slate-500"}`}>{p}</button>;
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700 bg-slate-800 text-slate-200">VAZGEÇ</Button>
            <Button data-testid="supplier-save-button" onClick={save} className="bg-blue-600 text-white hover:bg-blue-700">KAYDET</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)} title="TEDARİKÇİ SİLİNSİN Mİ?" onConfirm={del} />
    </div>
  );
}
